import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CreditCard, Lock, CheckCircle, Shield } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [addInsurance, setAddInsurance] = useState(false);
  const [error, setError] = useState("");
  const [agreedTerms, setAgreedTerms] = useState(false);
  const INSURANCE_PRICE = 19;
  const { convert } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/applications/${id}`).then(r => setApp(r.data)).catch(() => navigate("/dashboard"));
  }, [id, navigate]);

  const totalUSD = (app?.price || 100) + 29 + (addInsurance ? INSURANCE_PRICE : 0);

  const handlePay = async () => {
    setLoading(true); setError("");
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { setError("Failed to load payment gateway. Check your internet."); setLoading(false); return; }

      const { data } = await axios.post(`${API}/payments/razorpay/create-order`, {
        application_id: id, add_insurance: addInsurance
      });

      const options = {
        key: RAZORPAY_KEY || data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "FlyInVisa",
        description: data.description,
        order_id: data.order_id,
        prefill: { name: data.user_name, email: data.user_email },
        theme: { color: "#2563EB" },
        handler: async function (response) {
          try {
            await axios.post(`${API}/payments/razorpay/verify`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            setSuccess(true);
          } catch { setError("Payment verification failed. Please contact support."); }
          setLoading(false);
        },
        modal: { ondismiss: () => { setLoading(false); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => { setError(resp.error?.description || "Payment failed. Please try again."); setLoading(false); });
      rzp.open();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create payment order. Please try again.");
      setLoading(false);
    }
  };

  if (!app) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  if (success) return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-slate-50">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md" data-testid="payment-success">
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-8 h-8 text-emerald-600" /></div>
        <h2 className="heading-font text-2xl font-bold text-slate-900 mb-3">Payment Successful!</h2>
        <p className="text-sm text-slate-500 mb-6">Your {app.destination_country_name} visa application has been submitted.</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate(`/tracking/${id}`)} className="text-sm text-white font-semibold bg-blue-600 px-6 py-3 rounded-lg hover:bg-blue-700">Track Application</button>
          <button onClick={() => navigate("/dashboard")} className="text-sm text-slate-600 px-6 py-3 rounded-lg border border-slate-200 hover:bg-slate-50">Dashboard</button>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50">
      <div className="max-w-lg mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4 border border-blue-100"><CreditCard className="w-7 h-7 text-blue-600" /></div>
          <h1 className="heading-font text-3xl font-bold text-slate-900 mb-2">Payment</h1>
          <p className="text-sm text-slate-500">Secure checkout for your visa application</p>
        </div>

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3 mb-4" data-testid="payment-error">{error}</div>}

        {/* Order Summary */}
        <div className="card-elevated rounded-2xl p-6 mb-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Order Summary</h3>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm"><span className="text-slate-400">{app.destination_country_name} - {app.visa_type}</span><span className="text-slate-900">{convert(app.price || 100)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-400">Service Fee</span><span className="text-slate-900">{convert(29)}</span></div>
            {addInsurance && <div className="flex justify-between text-sm"><span className="text-slate-400">Travel Insurance</span><span className="text-slate-900">{convert(INSURANCE_PRICE)}</span></div>}
          </div>
          <div className="flex justify-between text-sm pt-3 border-t border-slate-100"><span className="text-slate-900 font-semibold">Total</span><span className="text-blue-600 font-bold text-lg">{convert(totalUSD)}</span></div>
        </div>

        {/* Insurance Upsell */}
        <div className={`card-elevated rounded-2xl p-5 mb-5 cursor-pointer transition-all ${addInsurance ? "border-blue-400 bg-blue-50/30 ring-1 ring-blue-200" : ""}`}
          onClick={() => setAddInsurance(!addInsurance)} data-testid="insurance-upsell">
          <div className="flex items-start gap-4">
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 shrink-0 transition-all ${addInsurance ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>
              {addInsurance && <CheckCircle className="w-3 h-3 text-white" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-slate-900">Travel Insurance</span></div>
                <span className="text-sm font-bold text-blue-600">{convert(INSURANCE_PRICE)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Medical coverage, trip cancellation, lost baggage up to $50,000.</p>
            </div>
          </div>
        </div>

        {/* Payment Info */}
        <div className="card-elevated rounded-2xl p-5 mb-5">
          <div className="flex items-center gap-2 mb-3"><Lock className="w-4 h-4 text-emerald-600" /><span className="text-xs font-medium text-emerald-600">Secure Payment via Razorpay</span></div>
          <p className="text-xs text-slate-400">Pay securely using UPI, Debit/Credit Cards, Net Banking, or Wallets.</p>
        </div>

        {/* Terms checkbox */}
        <label className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 mb-5 cursor-pointer" data-testid="terms-checkbox">
          <input type="checkbox" checked={agreedTerms} onChange={e => setAgreedTerms(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-blue-600 mt-0.5" />
          <span className="text-xs text-slate-500">I agree to the <a href="/legal/terms" target="_blank" className="text-blue-600 underline">Terms of Service</a>, <a href="/legal/refund" target="_blank" className="text-blue-600 underline">Refund Policy</a>, and <a href="/legal/visa-disclaimer" target="_blank" className="text-blue-600 underline">Visa Disclaimer</a>. I understand that visa approval is not guaranteed and fees are non-refundable once processing begins.</span>
        </label>

        <button onClick={handlePay} disabled={loading || !agreedTerms} data-testid="pay-btn"
          className="w-full py-3.5 btn-premium text-white font-semibold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? "Processing..." : `Pay ${convert(totalUSD)}`}
        </button>
        <p className="text-xs text-slate-400 text-center mt-3 flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> Secured by Razorpay</p>
      </div>
    </div>
  );
}
